import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";

import { authApi } from "../api/authApi";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(null);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setUser(data.user);
    setAccessToken(data.accessToken);
  };

  const ssoLogin = async (token) => {
    const data = await authApi.sso(token);
    setUser(data.user);
    setAccessToken(data.accessToken);
  };

  const refresh = useCallback(async () => {
    try {
      const data = await authApi.refresh();
      setUser(data.user);
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch (err) {
      console.log("Refresh falhou", err);
      logout();
    }
  }, []);

  const logout = async () => {
    await authApi.logout();
    setUser(null);
    setAccessToken(null);
  };

  const apiFetch = useCallback(
    async (endpoint, options = {}) => {
      const isFormData = options.body instanceof FormData;

      const headers = {
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...options.headers,
      };

      let res = await fetch(process.env.REACT_APP_API_URL + endpoint, {
        ...options,
        headers,
        credentials: "include",
      });

      if (res.status === 401) {
        try {
          const newToken = await refresh();

          const retryHeaders = {
            ...headers,
            Authorization: `Bearer ${newToken}`,
          };

          res = await fetch(process.env.REACT_APP_API_URL + endpoint, {
            ...options,
            headers: retryHeaders,
            credentials: "include",
          });
        } catch {
          throw new Error("Sessão expirada");
        }
      }

      if (res.status === 204) return undefined;

      const contentType = res.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await res.json()
        : { error: await res.text() };

      if (!res.ok) {
        throw new Error(
          payload?.error || payload?.message || `Erro HTTP ${res.status}`,
        );
      }

      return payload;
    },
    [accessToken, refresh],
  );

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const ssoToken = params.get("sso");

        if (ssoToken) {
          await ssoLogin(ssoToken);

          // opcional: limpa o token da URL
          window.history.replaceState({}, document.title, "/");
        } else {
          await refresh();
        }
      } catch (error) {
        console.log("Auth init error:", error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  const value = {
    user,
    accessToken,
    loading,
    login,
    refresh,
    logout,
    apiFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook customizado para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};
